import { Component, inject, Input } from '@angular/core';
import { FilterService } from '../../../../../_shared/services';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { DropdownOption, KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { formatPercentageDifference, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { ExecutiveStatsSummaryRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-variance-widget',
  standalone: true,
  imports: [KpiWidgetComponent, TranslocoPipe],
  templateUrl: './fuel-variance-widget.component.html',
  styleUrl: './fuel-variance-widget.component.scss'
})
export class FuelVarianceWidgetComponent extends BaseWidgetComponent {
  private readonly transloco = inject(TranslocoService);
  @Input() selectedFilter: string;

  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private auth = inject(AuthService);

  hasError: boolean = false;
  errorMessage: string = '';
  currentDate: Date = new Date();
  cost: number = 0;
  differenceChange: number = 0;
  selectedUnit: string = MeasurementTypes.Percentage.toString();
  dropdownOptions: DropdownOption[] = [];
  currencySymbol: string = '';
  currencyCode: string = '';

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this.percentageDifference = 0;
    this.difference = 0;

    this.currencyCode = this.auth.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
    this.dropdownOptions = [
      { label: '%', value: MeasurementTypes.Percentage.toString(), unit: '%' }
    ]
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;
    try {
      const request: ExecutiveStatsSummaryRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      }

      this.cancelSubject.next();
      const response = await this.executiveStatsService.getFuelVariance(request, this.cancelSubject);
      this.value = response?.data?.totalValue ?? 0;
      this.percentageDifference = response?.data?.percentageDifference ?? 0;
      this.difference = response?.data?.difference ?? 0;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.hasError = true;
          this.errorMessage = message;
          this.isLoading = false;
        }
      });
    }
  }

  formatValue(value: number): string {
    return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString, this.currencySymbol);
  }

  formatVarianceDifference(value: number): string {
    return formatPercentageDifference(value, this.selectedFilter, (key, params) => this.transloco.translate(key, params));
  }

  get isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }
}